import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { formatDayTime, nowMs, parseMs } from '@dorixona/utils'
import { Icon } from '../../ui/Icon'
import { Thumb } from '../../ui/Thumb'
import { Failed } from '../../ui/Load'
import { TableSkeleton } from '../../ui/Skeleton'
import { Pagination } from '../../ui/Pagination'
import { PageActions } from '../../app/PageHeader'
import { useSession } from '../auth/session'
import { useNewsList } from './api'
import { STATUS_TEXT, STATUS_TONE, newsStatus } from './model'

const PAGE_SIZES = [10, 25, 50] as const

const stay = (event: { stopPropagation: () => void }) => {
  event.stopPropagation()
}

/**
 * Новости приложения, новые сверху: черновики, запланированные и
 * опубликованные. Новую новость пишут на своей странице (`/news/new`) той же
 * формой, что и правку.
 */
export function NewsListScreen() {
  const { can } = useSession()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZES[0])
  const list = useNewsList({ page, pageSize, q })
  const now = nowMs()

  return (
    <>
      <PageActions>
        {list.data ? (
          <span className="ui-count adm-count" aria-label={`Новостей: ${list.data.total}`}>
            {list.data.total}
          </span>
        ) : null}
        <span className="spacer" />
        {can('news', 'c') ? (
          <button
            type="button"
            className="ui-btn"
            onClick={() => {
              void navigate('/news/new')
            }}
          >
            <Icon name="i-plus" />
            Новая новость
          </button>
        ) : null}
      </PageActions>

      <div className="ui-toolbar" role="search">
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            placeholder="Заголовок новости"
            aria-label="Поиск новости"
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPage(1)
            }}
          />
        </div>
      </div>

      <section className="ui-table-card" aria-label="Новости">
        {list.isPending ? <TableSkeleton what="новости" /> : null}
        {list.isError ? (
          <Failed
            what="новости"
            error={list.error}
            onRetry={() => {
              void list.refetch()
            }}
          />
        ) : null}

        {list.data ? (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table ui-news">
                <colgroup>
                  <col />
                  <col className="w-date" />
                  <col className="w-status" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">Новость</th>
                    <th scope="col">Публикация</th>
                    <th scope="col">Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.length === 0 ? (
                    <tr>
                      <td colSpan={3}>
                        {q ? (
                          <div className="ui-empty">
                            <Icon name="i-search" />
                            <b>Новости не найдены</b>
                            <p>Ищем по заголовку на русском и узбекском.</p>
                            <button
                              type="button"
                              className="ui-btn"
                              data-size="sm"
                              data-variant="secondary"
                              onClick={() => {
                                setQ('')
                              }}
                            >
                              Сбросить поиск
                            </button>
                          </div>
                        ) : (
                          <div className="ui-empty">
                            <Icon name="i-news" />
                            <b>Новостей пока нет</b>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : null}
                  {list.data.items.map((news) => {
                    const href = `/news/${news.id}`
                    const status = newsStatus(news, now)
                    return (
                      <tr
                        key={news.id}
                        data-href={href}
                        onClick={() => {
                          void navigate(href)
                        }}
                      >
                        <td>
                          <div className="ui-product">
                            <span className="nw-thumb">
                              <Thumb photos={news.photoUrl ? [news.photoUrl] : []} alt="" fluid />
                            </span>
                            <div className="txt">
                              <Link to={href} className="primary" onClick={stay}>
                                {news.titleRu}
                              </Link>
                              <span className="secondary">
                                {news.titleUz || (
                                  <span className="ui-tag" data-tone="warning">
                                    нет текста на узбекском
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>{news.publishedAt ? formatDayTime(parseMs(news.publishedAt)) : '—'}</td>
                        <td>
                          <span className="ui-status" data-tone={STATUS_TONE[status]}>
                            {STATUS_TEXT[status]}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              label="Страницы новостей"
              page={page}
              pageSize={pageSize}
              total={list.data.total}
              sizes={PAGE_SIZES}
              busy={list.isPlaceholderData}
              onPage={setPage}
              onPageSize={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </>
        ) : null}
      </section>
    </>
  )
}
